import type { Resume } from "@adafia/types";
import { Github, Globe, Linkedin, Mail, MapPin, Phone } from "lucide-react";

interface ResumePreviewProps {
	resume: Resume;
}

export function ResumePreview({ resume }: ResumePreviewProps) {
	const formatPeriod = (exp: Resume["experience"][number]) => {
		if (exp.startDate || exp.endDate) {
			const start = exp.startDate || "N/A";
			const end = exp.endDate || "N/A";
			return `${start} — ${end}`;
		}
		return exp.period || "";
	};

	return (
		<div className="preview-pane">
			<div className="resume-paper">
				<header className="resume-header">
					<h1 className="resume-name">{resume.personal.name}</h1>
					<p className="resume-title">{resume.personal.title}</p>
					<div className="contact-info">
						{resume.personal.email && (
							<div className="contact-item">
								<Mail size={14} color="#9ca3af" /> {resume.personal.email}
							</div>
						)}
						{resume.personal.phone && (
							<div className="contact-item">
								<Phone size={14} color="#9ca3af" /> {resume.personal.phone}
							</div>
						)}
						{resume.personal.location && (
							<div className="contact-item">
								<MapPin size={14} color="#9ca3af" /> {resume.personal.location}
							</div>
						)}
						{resume.personal.linkedin && (
							<div className="contact-item">
								<Linkedin size={14} color="#9ca3af" />{" "}
								{resume.personal.linkedin}
							</div>
						)}
						{resume.personal.github && (
							<div className="contact-item">
								<Github size={14} color="#9ca3af" /> {resume.personal.github}
							</div>
						)}
						{resume.personal.website && (
							<div className="contact-item">
								<Globe size={14} color="#9ca3af" /> {resume.personal.website}
							</div>
						)}
					</div>
				</header>

				{resume.personal.summary && (
					<section className="resume-section">
						<h2 className="resume-section-title">Professional Summary</h2>
						<p className="resume-text">{resume.personal.summary}</p>
					</section>
				)}

				<section className="resume-section">
					<h2 className="resume-section-title">Experience</h2>
					<div>
						{resume.experience.map((exp) => (
							<div key={exp.id} className="exp-item">
								<div className="exp-header">
									<h3 className="exp-role">{exp.role}</h3>
									<span className="exp-period">{formatPeriod(exp)}</span>
								</div>
								<div className="exp-sub">
									{exp.company}{" "}
									<span className="exp-loc">| {exp.location}</span>
								</div>
								<p className="resume-text" style={{ whiteSpace: "pre-wrap" }}>
									{exp.description}
								</p>
							</div>
						))}
					</div>
				</section>

				{resume.projects && resume.projects.length > 0 && (
					<section className="resume-section">
						<h2 className="resume-section-title">Projects</h2>
						<div>
							{resume.projects.map((project) => (
								<div key={project.id} className="exp-item">
									<div className="exp-header">
										<h3 className="exp-role">{project.name}</h3>
										{project.link && (
											<span className="exp-period">{project.link}</span>
										)}
									</div>
									<p className="resume-text" style={{ whiteSpace: "pre-wrap" }}>
										{project.description}
									</p>
								</div>
							))}
						</div>
					</section>
				)}

				<section className="resume-section">
					<h2 className="resume-section-title">Skills & Technologies</h2>
					<div className="skills-list">
						{resume.skills.map((skill, index) => (
							<span key={skill.id} className="skill-item">
								{skill.name}
								{index !== resume.skills.length - 1 && (
									<span className="skill-dot">•</span>
								)}
							</span>
						))}
					</div>
				</section>

				<section className="resume-section">
					<h2 className="resume-section-title">Education</h2>
					{resume.education.map((edu) => (
						<div key={edu.id} style={{ marginBottom: "8px" }}>
							<div className="exp-header">
								<h3 className="exp-role">{edu.school}</h3>
								<span
									className="exp-period"
									style={{
										background: "transparent",
										padding: 0,
										color: "#6b7280",
									}}
								>
									{edu.year}
								</span>
							</div>
							<p className="resume-text">{edu.degree}</p>
						</div>
					))}
				</section>

				{resume.certifications && resume.certifications.length > 0 && (
					<section className="resume-section">
						<h2 className="resume-section-title">Certifications</h2>
						{resume.certifications.map((cert) => (
							<div key={cert.id} style={{ marginBottom: "8px" }}>
								<div className="exp-header">
									<h3 className="exp-role">{cert.name}</h3>
									<span
										className="exp-period"
										style={{
											background: "transparent",
											padding: 0,
											color: "#6b7280",
										}}
									>
										{cert.year}
									</span>
								</div>
								<p className="resume-text">{cert.issuer}</p>
							</div>
						))}
					</section>
				)}
			</div>
		</div>
	);
}
